// Prism Ward (owner 2026-11-02: 'a gorgeous shield made out of glass'): a faceted crystal-glass dome around the hero
// while sim.wardCharges > 0. Render-only: it reacts to the view's calls and never decides gameplay.
// Look: an irregular geodesic gem dome (jittered icosphere, flat panes). Every pane is glass: fresnel rim, thin-film
// iridescence (cyan → violet → faint gold with the view angle), per-pane tint/transparency, sweeping specular glints,
// frost veins etched into the glass, bright bevelled seams (barycentric lines, no ink), a screen-space inner shimmer.
// Caustic light plays on the ground under it. Panes grow in from the ground up behind a light sweep; a hit sends a
// ripple across the panes from the struck side and leaves a crack web (more per lost charge) while the glass thins; the
// shatter turns the panes into real instanced glass shards that fly out, tumble, bounce once and fade.
// Cost: dome 2 draws (back + front, ~60 panes each), caustic disc 1 draw, shards 1 instanced draw (~170 tris) while flying.
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as THREE from 'three/webgpu';
import { abs, acos, attribute, cameraPosition, clamp, color, dot, float, fract, fwidth, length, min, mix, mx_noise_float, normalGeometry, normalWorld, normalize, oneMinus, positionGeometry, positionWorld, pow, screenUV, sin, smoothstep, uniform, uv, vec3 } from 'three/tsl';
import { mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Fx } from './fx';
import { FLASH } from './fx';

const R = 1.8, LIFT = 0.5, CUT = -0.38;   // dome radius, centre height above the ground, panes whose centroid is below CUT*R are dropped (under ground)
const COL = 0xbfeaff, FORM_T = 0.25, SHARD_CAP = 256;
const _v = new THREE.Vector3(), _w = new THREE.Vector3(), _q = new THREE.Quaternion(), _m = new THREE.Matrix4(), _rot = new THREE.Matrix4(), _ax = new THREE.Vector3();

interface Pane { a: THREE.Vector3; b: THREE.Vector3; c: THREE.Vector3; n: THREE.Vector3; cen: THREE.Vector3 }

/** irregular gem dome: a jittered icosphere, non-indexed so every pane is flat and carries its own seed/centroid/height */
function buildDome(): { geo: THREE.BufferGeometry; panes: Pane[]; shards: Pane[] } {
  let g: THREE.BufferGeometry = new THREE.IcosahedronGeometry(1, 1);
  g.deleteAttribute('normal'); g.deleteAttribute('uv'); g = mergeVertices(g);
  const pos = g.attributes.position as THREE.BufferAttribute, v = new THREE.Vector3(), t = new THREE.Vector3();
  let seed = 7; const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let i = 0; i < pos.count; i++) { // jitter each vertex along the sphere: uneven gem facets instead of a perfect geodesic
    v.fromBufferAttribute(pos, i).normalize();
    t.set(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5); t.addScaledVector(v, -t.dot(v));
    v.addScaledVector(t, 0.3).normalize().multiplyScalar(R);
    pos.setXYZ(i, v.x, v.y, v.z);
  }
  const idx = g.index!, P: number[] = [], N: number[] = [], B: number[] = [], F: number[] = [], panes: Pane[] = [], shards: Pane[] = [];
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), n = new THREE.Vector3(), cen = new THREE.Vector3();
  const ymin = CUT * R, ymax = R * 1.02;
  for (let f = 0; f < idx.count; f += 3) {
    a.fromBufferAttribute(pos, idx.getX(f)); b.fromBufferAttribute(pos, idx.getX(f + 1)); c.fromBufferAttribute(pos, idx.getX(f + 2));
    cen.copy(a).add(b).add(c).multiplyScalar(1 / 3);
    if (cen.y < ymin) continue;
    n.copy(b).sub(a).cross(c.clone().sub(a)).normalize();
    if (n.dot(cen) < 0) { const tmp = b.clone(); b.copy(c); c.copy(tmp); n.negate(); } // outward winding
    const s = rnd(), h = (cen.y - ymin) / (ymax - ymin);
    for (const [p, bx, by, bz] of [[a, 1, 0, 0], [b, 0, 1, 0], [c, 0, 0, 1]] as [THREE.Vector3, number, number, number][]) {
      P.push(p.x, p.y, p.z); N.push(n.x, n.y, n.z); B.push(bx, by, bz, h); F.push(cen.x, cen.y, cen.z, s);
    }
    const pane = { a: a.clone(), b: b.clone(), c: c.clone(), n: n.clone(), cen: cen.clone() };
    panes.push(pane);
    if (s < 0.4) shards.push(pane);                                   // some panes break off whole
    else { // most split into four pieces at the midpoints (smaller, more numerous shards)
      const ab = a.clone().lerp(b, 0.5), bc = b.clone().lerp(c, 0.5), ca = c.clone().lerp(a, 0.5);
      for (const [x, y, z] of [[a, ab, ca], [ab, b, bc], [ca, bc, c], [ab, bc, ca]] as THREE.Vector3[][])
        shards.push({ a: x.clone(), b: y.clone(), c: z.clone(), n: n.clone(), cen: x.clone().add(y).add(z).multiplyScalar(1 / 3) });
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(N, 3));
  geo.setAttribute('bary', new THREE.Float32BufferAttribute(B, 4));
  geo.setAttribute('fc', new THREE.Float32BufferAttribute(F, 4));
  return { geo, panes, shards };
}

/** thin-film tint: cyan → violet → faint gold, driven by a 0..1 phase */
function iridescence(phase: any): any {
  const tri = abs(fract(phase).mul(2).sub(1));
  const t1 = mix(color(0x46d2ff), color(0x9d78ff), smoothstep(0.0, 0.5, tri));
  return mix(t1, color(0xffd59a), smoothstep(0.55, 1.0, tri));
}

export class PrismWard {
  private group = new THREE.Group();
  private front: THREE.Mesh; private back: THREE.Mesh; private caustic: THREE.Mesh;
  private shardMesh: THREE.InstancedMesh; private shardAttr: THREE.InstancedBufferAttribute;
  private shardShapes: Pane[]; private shardLocal: THREE.Matrix4[] = [];
  // shard pool (struct of arrays, no per-frame allocation): position, velocity, tumble axis, spin, angle, life, max life, seed, local matrix
  private sP = new Float32Array(SHARD_CAP * 3); private sV = new Float32Array(SHARD_CAP * 3); private sA = new Float32Array(SHARD_CAP * 3);
  private sS = new Float32Array(SHARD_CAP * 4); private sM: THREE.Matrix4[] = []; private sN = 0; private sGround = 0; // sS = (age, seed, spin rate, max life)
  private sLife = new Float32Array(SHARD_CAP); private sAng = new Float32Array(SHARD_CAP);
  private k = 0;              // visible amount 0..1 (1 while charges remain, 0 after the shatter)
  private grow = 1;           // 0..1 form-in progress
  private hitT = 0;           // ripple / flash timer after an absorbed hit
  private maxCharges = 1; private lastCharges = 0; private burst = false; private hits = 0;
  // uniforms shared by the dome, caustics and shards
  private uGrow = uniform(1); private uStrength = uniform(1); private uFlash = uniform(0); private uT = uniform(0);
  private uRipple = uniform(1); private uRipA = uniform(0); private uCrack = uniform(0);
  private uHit = uniform(new THREE.Vector3(0, 0, 1)); private uH1 = uniform(new THREE.Vector3(0, -2, 0)); private uH2 = uniform(new THREE.Vector3(0, -2, 0));
  private uL1 = uniform(new THREE.Vector3(1, 0.5, 0)); private uL2 = uniform(new THREE.Vector3(0, 0.8, 1)); private uL3 = uniform(new THREE.Vector3(0, 0.3, -1));

  constructor(scene: THREE.Scene) {
    const { geo, shards } = buildDome();
    this.shardShapes = shards.slice(0, SHARD_CAP);
    this.back = new THREE.Mesh(geo, this.domeMaterial(THREE.BackSide, 0.3)); this.back.renderOrder = 3;
    this.front = new THREE.Mesh(geo, this.domeMaterial(THREE.FrontSide, 1)); this.front.renderOrder = 4;
    this.back.frustumCulled = this.front.frustumCulled = false;
    this.caustic = new THREE.Mesh(new THREE.CircleGeometry(R * 1.3, 48), this.causticMaterial());
    this.caustic.rotation.x = -Math.PI / 2; this.caustic.renderOrder = 2; this.caustic.frustumCulled = false;
    this.group.add(this.back, this.front); this.group.visible = false; this.caustic.visible = false;
    scene.add(this.group, this.caustic);
    // shards: one unit triangle, instanced; each instance matrix maps it onto a real pane piece (an affine map takes
    // the unit triangle to any triangle), so the shards are the actual facets of the dome
    const sg = new THREE.BufferGeometry();
    sg.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0, 1, 0, 0, 0, 1, 0], 3));
    sg.setAttribute('normal', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 0, 0, 1], 3));
    sg.setAttribute('bary', new THREE.Float32BufferAttribute([1, 0, 0, 0, 1, 0, 0, 0, 1], 3));
    this.shardAttr = new THREE.InstancedBufferAttribute(this.sS, 4); this.shardAttr.setUsage(THREE.DynamicDrawUsage);
    sg.setAttribute('sh', this.shardAttr);
    this.shardMesh = new THREE.InstancedMesh(sg, this.shardMaterial(), SHARD_CAP);
    this.shardMesh.count = 0; this.shardMesh.frustumCulled = false; this.shardMesh.renderOrder = 4;
    scene.add(this.shardMesh);
    for (let i = 0; i < SHARD_CAP; i++) this.sM.push(new THREE.Matrix4());
    for (const s of this.shardShapes) { // unit triangle → the piece, centred on its own centroid
      const e1 = s.b.clone().sub(s.a), e2 = s.c.clone().sub(s.a), o = s.a.clone().sub(s.cen);
      this.shardLocal.push(new THREE.Matrix4().set(e1.x, e2.x, s.n.x, o.x, e1.y, e2.y, s.n.y, o.y, e1.z, e2.z, s.n.z, o.z, 0, 0, 0, 1));
    }
  }

  /** the shared glass response: view vector, fresnel, glints from two sweeping lights, thin-film tint */
  private glass(seed: any) {
    const nW = normalize(normalWorld), V = normalize(cameraPosition.sub(positionWorld));
    const facing = abs(dot(nW, V));
    const fres = pow(oneMinus(facing), 2.2);
    const g1 = pow(abs(dot(nW, normalize(this.uL1.add(V)))), 40), g2 = pow(abs(dot(nW, normalize(this.uL2.add(V)))), 64), g3 = pow(abs(dot(nW, normalize(this.uL3.add(V)))), 120);
    const glint = smoothstep(0.1, 0.5, g1).mul(0.9).add(smoothstep(0.15, 0.65, g2).mul(0.7)).add(smoothstep(0.2, 0.6, g3).mul(0.5)).min(1.2); // hard-edged cel glints that sweep pane to pane
    const tint = iridescence(facing.mul(0.8).add(seed.mul(0.7)).add(this.uT.mul(0.03)));
    return { nW, V, facing, fres, glint, tint };
  }

  private domeMaterial(side: THREE.Side, gain: number): THREE.MeshBasicNodeMaterial {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side, fog: false });
    const B = attribute('bary', 'vec4') as any, FC = attribute('fc', 'vec4') as any;
    const seed = FC.w, h = B.w;
    // forming: every pane grows from its centroid, ground first, drifting in from slightly outside the dome
    const fk0 = smoothstep(0.0, 0.32, this.uGrow.mul(1.32).sub(h));
    const fk = fk0.mul(fk0).mul(float(3).sub(fk0.mul(2)));
    m.positionNode = FC.xyz.add(positionGeometry.sub(FC.xyz).mul(fk)).add(normalGeometry.mul(oneMinus(fk).mul(0.4)));
    const { nW, facing, fres, glint, tint } = this.glass(seed);
    const pG = positionGeometry, nU = normalize(pG);
    // bevelled seams: thin bright lines on the pane edges plus a soft bevel band that catches the glints
    const e = min(B.x, min(B.y, B.z)), w = fwidth(e);
    const seam = oneMinus(smoothstep(w.mul(0.5), w.mul(1.9), e));
    const bevel = oneMinus(smoothstep(0.0, 0.13, e)).mul(float(0.3).add(glint).min(1));
    // frost veins and fine etching inside the panes (object space so they turn with the dome), slowly breathing
    const vn = mx_noise_float(pG.mul(2.4).add(seed.mul(5.0))) as any;
    const et = mx_noise_float(pG.mul(6.5).add(vec3(3.1, seed.mul(2.0), 1.7))) as any;
    const breathe = sin(this.uT.mul(1.6).add(seed.mul(9.0))).mul(0.3).add(0.7);
    const frost = smoothstep(0.07, 0.0, abs(vn)).add(smoothstep(0.045, 0.0, abs(et)).mul(0.55)).min(1).mul(breathe);
    // inner shimmer: a refraction-like wobble of a screen-space noise through the pane
    const sh = mx_noise_float(vec3(screenUV.mul(7.0).add(nW.xy.mul(0.45)), this.uT.mul(0.4))) as any;
    const shim = smoothstep(0.1, 0.75, sh).mul(facing);
    // impact ripple racing across the glass from the struck side, with a bloom at the point of impact
    const d = acos(clamp(dot(nU, this.uHit), -1, 1));
    const ring = smoothstep(0.5, 0.0, abs(d.sub(this.uRipple.mul(3.3)))).mul(oneMinus(this.uRipple));
    const imp = smoothstep(1.0, 0.0, d).mul(pow(oneMinus(this.uRipple), 2)).mul(0.6);
    const rip = ring.add(imp).mul(this.uRipA);
    // cracks: a noise web spreading from the hit points; its reach and density grow with every lost charge
    const dm = min(acos(clamp(dot(nU, this.uH1), -1, 1)), acos(clamp(dot(nU, this.uH2), -1, 1)));
    const reach = this.uCrack.mul(2.6).add(0.3);
    const cmask = smoothstep(reach, reach.sub(0.8), dm).mul(min(this.uCrack.mul(20), 1));
    const cn = mx_noise_float(pG.mul(3.4).add(vec3(11.0, 5.0, 2.0))) as any, cn2 = mx_noise_float(pG.mul(6.8).add(vec3(1.0, 9.0, 4.0))) as any;
    const cracks = smoothstep(0.05, 0.0, abs(cn)).add(smoothstep(0.03, 0.0, abs(cn2)).mul(smoothstep(0.3, 0.8, this.uCrack))).min(1).mul(cmask);
    const crackHalo = smoothstep(0.16, 0.04, abs(cn)).mul(cmask).mul(0.6); // the darker glass beside a crack
    // light sweep on the growth front while forming
    const sweep = smoothstep(0.2, 0.0, abs(h.sub(this.uGrow.mul(1.32)).add(0.12))).mul(oneMinus(this.uGrow).min(1).mul(3).min(1));
    const hi = seam.mul(0.7).add(bevel.mul(0.3)).add(glint).add(rip.mul(0.7)).add(sweep).add(cracks).add(this.uFlash.mul(0.5)).min(1);
    let col: any = mix(tint, color(0xeafcff), frost.mul(0.55));
    col = mix(col, color(0x7fe0ff), fres.mul(0.45)); // the rim keeps a glassy cyan
    col = mix(col, tint.mul(0.35), crackHalo);
    col = mix(col, color(0xffffff), hi.mul(0.75));
    col = col.mul(float(1.0).add(glint.mul(0.5)).add(this.uFlash.mul(0.8)).add(rip.mul(0.4)));
    m.colorNode = col;
    const frosted = smoothstep(0.78, 0.82, seed); // a few panes are milkier
    const body = float(0.1).add(seed.mul(0.07)).add(frosted.mul(0.1)).add(smoothstep(0.55, 0.0, h).mul(0.08));
    const op = body.add(fres.mul(0.6)).add(seam.mul(0.6)).add(bevel.mul(0.2)).add(frost.mul(0.26).mul(facing.mul(0.5).add(0.5)))
      .add(shim.mul(0.12)).add(glint.mul(0.8)).add(rip.mul(0.6)).add(sweep.mul(0.9)).add(cracks.mul(0.95)).add(crackHalo.mul(0.6)).add(this.uFlash.mul(0.25));
    m.opacityNode = op.mul(this.uStrength).mul(gain).min(0.95);
    return m;
  }

  private causticMaterial(): THREE.MeshBasicNodeMaterial {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
    const p = uv().sub(0.5).mul(2), r = length(p);
    const n1 = mx_noise_float(vec3(p.mul(2.6).add(this.uT.mul(0.12)), this.uT.mul(0.3))) as any;
    const n2 = mx_noise_float(vec3(p.mul(4.2).add(2.0), this.uT.mul(-0.22))) as any;
    const ca = pow(oneMinus(abs(n1)), 5).add(pow(oneMinus(abs(n2)), 7).mul(0.7));    // bright refracted light lines
    const fall = smoothstep(1.0, 0.4, r);
    const foot = smoothstep(0.06, 0.0, abs(r.sub(float(1 / 1.3)))).mul(0.9);          // where the glass meets the ground
    const tint = mix(color(0x7fe8ff), color(0xc49cff), smoothstep(-0.3, 0.3, n2));
    m.colorNode = mix(tint, color(0xffffff), foot.mul(0.6).add(this.uFlash.mul(0.7)).min(1)).mul(1.25);
    m.opacityNode = ca.mul(fall).mul(1.0).add(foot).add(fall.mul(0.12)).add(this.uFlash.mul(0.25).mul(fall)).mul(this.uStrength).min(0.9);
    return m;
  }

  private shardMaterial(): THREE.MeshBasicNodeMaterial {
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const S = attribute('sh', 'vec4') as any, B = attribute('bary', 'vec3') as any;
    const age = S.x, seed = S.y;
    const { fres, glint, tint } = this.glass(seed);
    const e = min(B.x, min(B.y, B.z)), w = fwidth(e);
    const seam = oneMinus(smoothstep(w.mul(0.5), w.mul(2.0), e));
    const fade = oneMinus(smoothstep(0.5, 1.0, age));
    const hi = seam.mul(0.9).add(glint).add(fres.mul(0.4)).add(oneMinus(smoothstep(0.0, 0.06, age)).mul(0.35)).min(1);
    m.colorNode = mix(tint, color(0xffffff), hi.mul(0.8)).mul(float(1.05).add(glint.mul(0.5)));
    m.opacityNode = float(0.3).add(seed.mul(0.12)).add(fres.mul(0.45)).add(seam.mul(0.85)).add(glint.mul(0.8)).min(0.95).mul(fade);
    return m;
  }

  onUp(): void { this.grow = 0; this.k = 1; this.hits = 0; this.burst = false; this.lastCharges = 0; this.uCrack.value = 0; (this.uH1.value as THREE.Vector3).set(0, -2, 0); (this.uH2.value as THREE.Vector3).set(0, -2, 0); }

  /** an absorbed hit; dx/dz (optional) is the direction the blow came from, so the ripple and cracks start on that side */
  onHit(x: number, y: number, z: number, fx: Fx, dx = NaN, dz = NaN): void {
    this.hitT = 0.3; this.hits++;
    if (!(dx * dx + dz * dz > 1e-6)) { const a = Math.random() * Math.PI * 2; dx = Math.cos(a); dz = Math.sin(a); }
    // the struck point in dome space (the dome keeps turning, cracks must turn with it)
    const yaw = this.group.rotation.y, l = Math.hypot(dx, dz);
    _v.set((dx * Math.cos(yaw) - dz * Math.sin(yaw)) / l, 0.15, (dx * Math.sin(yaw) + dz * Math.cos(yaw)) / l).normalize();
    (this.uHit.value as THREE.Vector3).copy(_v);
    ((this.hits === 1 ? this.uH1 : this.uH2).value as THREE.Vector3).copy(_v);
    const px = x + dx / l * R * 0.9, pz = z + dz / l * R * 0.9, py = y + LIFT + 0.25;
    fx.stars.add(px, py, pz, 0.8, 0.14, 0xffffff, 2.4);
    fx.particles.emit(px, py, pz, 14, 5, COL, 0.16, 0.45, 1.5, 6, 3, 2);
    fx.rings.add(x, y + LIFT, z, R * 1.05, 0.2, COL, 2, 1);
    if (FLASH.dim >= 1) fx.lights.flash(px, py, pz, COL, 14, 0.15, 10);
  }

  /** the shatter (every lost charge when evolved: the dome stays and an outer layer bursts off) */
  onBreak(x: number, gy: number, z: number, r: number, evolved: boolean, fx: Fx): void {
    this.burst = true; this.hitT = Math.max(this.hitT, 0.3);
    fx.shatter(x, gy + LIFT + 0.6, z, evolved ? 30 : 20, 12, COL, 0.3);
    fx.shatter(x, gy + LIFT + 0.6, z, 12, 9, 0xffffff, 0.22);
    fx.stars.add(x, gy + LIFT + 0.4, z, 1.8, 0.16, 0xffffff, 3);
    fx.particles.emit(x, gy + LIFT + 0.5, z, evolved ? 50 : 36, 9, 0xeafcff, 0.14, 0.7, 3, 9, 2.5, 2.5); // sparkles
    fx.rings.add(x, gy + 0.2, z, r, 0.32, COL, 3, 1);
    fx.rings.add(x, gy + 0.25, z, r * 0.6, 0.22, 0xffffff, 2, 1);
    if (FLASH.dim >= 1) fx.lights.flash(x, gy + 1.2, z, COL, 30, 0.22, r * 2);
    fx.shake = Math.max(fx.shake, 0.3);
    this.spawnShards(x, gy, z, evolved ? 1.3 : 1, evolved && this.lastCharges > 1 ? 2 : 1); // evolved with charges left: only an outer layer bursts off
  }

  private spawnShards(x: number, gy: number, z: number, vel: number, every: number): void {
    const yaw = this.group.rotation.y, s = this.group.scale.x;
    _rot.makeRotationY(yaw).scale(_w.set(s, s, s));
    this.sGround = gy; this.sN = 0;
    const cy = gy + LIFT, ca = Math.cos(yaw), sa = Math.sin(yaw);
    for (let i = 0; i < this.shardShapes.length && this.sN < SHARD_CAP; i += every) {
      const sh = this.shardShapes[i], k = this.sN++;
      // the piece's world position and the matrix that maps the unit triangle onto it (dome yaw applied once here)
      const wx = x + (sh.cen.x * ca + sh.cen.z * sa) * s, wy = cy + sh.cen.y * s, wz = z + (-sh.cen.x * sa + sh.cen.z * ca) * s;
      this.sM[k].multiplyMatrices(_rot, this.shardLocal[i]);
      const nx = sh.n.x * ca + sh.n.z * sa, nz = -sh.n.x * sa + sh.n.z * ca, sp = (5 + Math.random() * 6) * vel;
      this.sP[k * 3] = wx; this.sP[k * 3 + 1] = wy; this.sP[k * 3 + 2] = wz;
      this.sV[k * 3] = nx * sp + (Math.random() - 0.5) * 2; this.sV[k * 3 + 1] = sh.n.y * sp + 1.5 + Math.random() * 3.5; this.sV[k * 3 + 2] = nz * sp + (Math.random() - 0.5) * 2;
      _ax.set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
      this.sA[k * 3] = _ax.x; this.sA[k * 3 + 1] = _ax.y; this.sA[k * 3 + 2] = _ax.z;
      const life = 0.75 + Math.random() * 0.5;
      this.sS[k * 4] = 0; this.sS[k * 4 + 1] = Math.random(); this.sS[k * 4 + 2] = (4 + Math.random() * 10) * (Math.random() < 0.5 ? -1 : 1); this.sS[k * 4 + 3] = life;
      this.sLife[k] = life; this.sAng[k] = Math.random() * 6.28;
    }
  }

  private updateShards(dt: number): void {
    const P = this.sP, V = this.sV, A = this.sA, S = this.sS, g = this.sGround + 0.03, drag = Math.exp(-1.1 * dt);
    for (let k = 0; k < this.sN; k++) {
      this.sLife[k] -= dt;
      if (this.sLife[k] <= 0) { // swap-remove
        const l = --this.sN; if (k === l) break;
        for (let j = 0; j < 3; j++) { P[k * 3 + j] = P[l * 3 + j]; V[k * 3 + j] = V[l * 3 + j]; A[k * 3 + j] = A[l * 3 + j]; }
        for (let j = 0; j < 4; j++) S[k * 4 + j] = S[l * 4 + j];
        this.sLife[k] = this.sLife[l]; this.sAng[k] = this.sAng[l]; this.sM[k].copy(this.sM[l]); k--; continue;
      }
      V[k * 3 + 1] -= 11 * dt; V[k * 3] *= drag; V[k * 3 + 1] *= drag; V[k * 3 + 2] *= drag;
      P[k * 3] += V[k * 3] * dt; P[k * 3 + 1] += V[k * 3 + 1] * dt; P[k * 3 + 2] += V[k * 3 + 2] * dt;
      if (P[k * 3 + 1] < g && V[k * 3 + 1] < 0) { P[k * 3 + 1] = g; V[k * 3 + 1] *= -0.3; V[k * 3] *= 0.6; V[k * 3 + 2] *= 0.6; S[k * 4 + 2] *= 0.4; } // one dull bounce, then it skids
      this.sAng[k] += S[k * 4 + 2] * dt;
      S[k * 4] = 1 - this.sLife[k] / S[k * 4 + 3];
      _ax.set(A[k * 3], A[k * 3 + 1], A[k * 3 + 2]); _q.setFromAxisAngle(_ax, this.sAng[k]);
      _m.makeRotationFromQuaternion(_q).setPosition(P[k * 3], P[k * 3 + 1], P[k * 3 + 2]).multiply(this.sM[k]);
      this.shardMesh.setMatrixAt(k, _m);
    }
    this.shardMesh.count = this.sN; this.shardMesh.visible = this.sN > 0;
    if (this.sN > 0) { this.shardMesh.instanceMatrix.needsUpdate = true; this.shardAttr.needsUpdate = true; }
  }

  update(charges: number, hx: number, gy: number, hz: number, dt: number, t: number): void {
    const want = charges > 0;
    if (want) {
      if (this.lastCharges === 0) this.maxCharges = Math.max(1, charges); // the dome just went up: remember its full strength
      this.grow = Math.min(1, this.grow + dt / FORM_T);
    } else this.k = this.burst ? 0 : Math.max(0, this.k - dt / 0.15);  // a shatter replaces the dome with its shards at once
    this.lastCharges = charges; if (!want) this.burst = false;
    this.hitT = Math.max(0, this.hitT - dt);
    this.uT.value = t;
    (this.uL1.value as THREE.Vector3).set(Math.cos(t * 1.1), 0.55, Math.sin(t * 1.1)).normalize();   // sweeping glint lights
    (this.uL2.value as THREE.Vector3).set(Math.cos(2.0 - t * 0.7), 0.85, Math.sin(2.0 - t * 0.7)).normalize();
    (this.uL3.value as THREE.Vector3).set(Math.cos(t * 2.3), 0.25 + 0.5 * Math.sin(t * 0.9), Math.sin(t * 2.3)).normalize();
    this.updateShards(dt);
    const vis = want ? 1 : this.k;
    this.group.visible = this.caustic.visible = vis > 0.01;
    if (!this.group.visible) return;
    const g = this.grow, ease = 1 - (1 - g) ** 3;
    const wobble = this.hitT > 0 ? Math.sin(this.hitT * 70) * 0.025 * this.hitT / 0.3 : 0;
    const s = (0.85 + 0.15 * ease) * (1 + wobble);
    this.group.position.set(hx, gy + LIFT, hz); this.group.scale.set(s, s, s);
    this.group.rotation.y = t * 0.22;
    this.caustic.position.set(hx, gy + 0.08, hz); this.caustic.scale.setScalar(0.3 + 0.7 * ease); this.caustic.rotation.z = t * 0.05;
    // the glass thins and flickers as charges are lost
    const lost = Math.max(0, Math.min(1, (this.maxCharges - charges) / Math.max(1, this.maxCharges)));
    const flick = 1 - lost * 0.22 * (0.5 + 0.5 * Math.sin(t * 23 + Math.sin(t * 7.3) * 3));
    this.uStrength.value = vis * (1 - lost * 0.4) * flick;
    this.uCrack.value = lost;
    this.uGrow.value = g;
    const dim = FLASH.dim >= 1 ? 1 : 0.5;
    this.uRipple.value = 1 - this.hitT / 0.3; this.uRipA.value = this.hitT > 0 ? dim : 0;
    this.uFlash.value = (this.hitT / 0.3) ** 2 * 0.2 * dim;
  }

  clear(): void { this.k = 0; this.grow = 1; this.hitT = 0; this.burst = false; this.sN = 0; this.shardMesh.count = 0; this.shardMesh.visible = false; this.group.visible = false; this.caustic.visible = false; }
}
